/**
 * HeroC — canonical sponsor-first hero.
 *
 * Ported from Yan-Vada Hero Pilot/components/hero-c.jsx (Session 15b Claude Design).
 *
 * Composition:
 *   Background:  /sunset-1.jpeg (landscape, no neon in frame — clean sky canvas)
 *   Video swap:  pass mediaType="video" and a heroVideoUrl from the public CMS.
 *   Scrim:       dual-band warm-black gradient (darker top + bottom, clear center)
 *   Top zone:    SVG neon wordmark + optional "Presented by" sponsor card
 *   Bottom zone: Sinhala three-line tagline + English voice line
 *
 * Props:
 *   compact         — mobile layout (default false)
 *   seasonLabel     — visible season/sponsor line under the lockup
 *   showCtaActions  — optional sponsor hero buttons under the voice line
 *   sponsorLogo     — path to sponsor logotype (e.g. "/licensed-logotype.jpeg")
 *                     If omitted, the "Presented by" card is not rendered.
 *   sponsorName     — sponsor display name for alt text (e.g. "The Licenced")
 *   showScrollCue   — optional down-arrow control for sponsor pages.
 */

import { useEffect, useRef, useState, type MouseEvent, type CSSProperties } from 'react'
import NeonWordmark from './NeonWordmark'
import PlatformIcon, { type SocialPlatform } from './PlatformIcon'
import { SNEAK_PEEK_VIDEO_URL } from '../lib/sneakPeek'
import type { HeroLayout } from '../shared/tokens/sponsor-config.types'

/** Map an optional HeroLayout to inline CSS custom properties for the lockup.
 *  Only defined fields are emitted, so absent layout = stylesheet defaults. */
function heroLayoutVars(layout?: HeroLayout): Record<string, string> {
  const vars: Record<string, string> = {}
  if (!layout) return vars
  if (layout.sponsor_shift_x) vars['--hero-c-sponsor-shift-x'] = layout.sponsor_shift_x
  if (layout.separator_offset_x) vars['--hero-c-x-offset-x'] = layout.separator_offset_x
  if (layout.midline_shift) vars['--hero-c-midline-shift'] = layout.midline_shift
  if (layout.separator_color) vars['--hero-c-x-color'] = layout.separator_color
  return vars
}

// Three-line Sinhala poem — bridges industries / audience, deliberative function.
// Canonical form per PF-R2 §6 (Session 15b). Rendered as background plate bottom zone.
const SINHALA_POEM = [
  'ඉන්ඩස්ට්‍රියයි ඕඩියනස් එකයි අතර වාදෙ',
  'ඒකෙ අතරමැදි කොටස්කරැ යන්',
  'සංවාදෙ',
]

interface HeroCProps {
  compact?: boolean
  seasonLabel?: string
  showCtaActions?: boolean
  watchHref?: string
  partnershipHref?: string
  headline?: string
  sponsorLogo?: string   // public path — e.g. "/licensed-logotype.jpeg"
  sponsorName?: string   // alt text — e.g. "The Licenced"
  heroLayout?: HeroLayout // optional per-sponsor lockup positioning (admin px-dials)
  mediaType?: 'image' | 'video'
  heroImageUrl?: string
  heroVideoUrl?: string
  heroVideoWebmUrl?: string
  showScrollCue?: boolean
  scrollTargetId?: string
  partnershipDisabled?: boolean
  partnershipDisabledLabel?: string
  socialLinks?: Array<{
    platform: SocialPlatform
    href: string
    label?: string
  }>
}

function easeBriefInMediumOut(t: number) {
  if (t < 0.18) return 0.09 * Math.pow(t / 0.18, 2)
  return 0.09 + 0.91 * (1 - Math.pow(1 - ((t - 0.18) / 0.82), 3))
}

function scrollToElement(target: HTMLElement) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (prefersReducedMotion) {
    target.scrollIntoView({ block: 'start' })
    return
  }

  const startY = window.scrollY
  const endY = target.getBoundingClientRect().top + startY
  const distance = endY - startY
  const duration = 560
  const startedAt = performance.now()

  function step(now: number) {
    const progress = Math.min(1, (now - startedAt) / duration)
    window.scrollTo(0, startY + distance * easeBriefInMediumOut(progress))
    if (progress < 1) window.requestAnimationFrame(step)
  }

  window.requestAnimationFrame(step)
}

export default function HeroC({ compact = false, seasonLabel = 'Season 01', showCtaActions = false, watchHref = '/#watch', partnershipHref = '#partnership-opportunity', headline, sponsorLogo, sponsorName, heroLayout, mediaType = 'image', heroImageUrl = '/sunset-1.jpeg', heroVideoUrl = '', heroVideoWebmUrl = '', showScrollCue = false, scrollTargetId, partnershipDisabled = false, partnershipDisabledLabel = 'Read Partnership Opportunity', socialLinks = [] }: HeroCProps) {
  const [sneakPeekOpen, setSneakPeekOpen] = useState(false)
  const sneakPeekVideoRef = useRef<HTMLVideoElement | null>(null)
  const activeSocialLinks = socialLinks.filter(link => !!link.href)
  const minHeight = compact ? 'clamp(680px, 100dvh, 820px)' : 'clamp(700px, 100dvh, 960px)'
  const padX = compact ? 'clamp(20px, 6vw, 24px)' : 'clamp(20px, 7vw, 96px)'
  const padTop = compact ? 'clamp(48px, 8vw, 56px)' : 'clamp(56px, 7vw, 80px)'
  const padBottom = showScrollCue
    ? (activeSocialLinks.length > 0
      ? (compact ? 'clamp(92px, 15vw, 116px)' : 'clamp(74px, 7.5vw, 100px)')
      : (compact ? 'clamp(88px, 16vw, 104px)' : 'clamp(92px, 10vw, 124px)'))
    : (compact ? 'clamp(56px, 10vw, 64px)' : 'clamp(64px, 9vw, 100px)')
  const hasSponsor = !!sponsorLogo
  const wordmarkHeight = hasSponsor ? 'clamp(62px, 16vw, 176px)' : (compact ? 84 : 'clamp(78px, 10vw, 112px)')
  const useVideo = mediaType === 'video' && !!heroVideoUrl
  const showHeadline = !!headline && !hasSponsor

  function handleScrollCueClick(event: MouseEvent<HTMLButtonElement>) {
    const hero = event.currentTarget.closest('[data-hero-c-root="true"]')
    const target = scrollTargetId ? document.getElementById(scrollTargetId) : hero?.nextElementSibling
    if (target instanceof HTMLElement) scrollToElement(target)
  }

  function handleSneakPeekClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    if (watchHref && watchHref !== '/#watch' && watchHref !== '#watch') {
      window.location.href = watchHref
      return
    }
    setSneakPeekOpen(true)
  }

  function closeSneakPeek() {
    const video = sneakPeekVideoRef.current
    if (video) {
      video.pause()
      video.currentTime = 0
    }
    setSneakPeekOpen(false)
  }

  useEffect(() => {
    if (!sneakPeekOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closeSneakPeek()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [sneakPeekOpen])

  return (
    <div className="hero-c" data-hero-c-root="true" style={{
      width: '100%',
      minHeight,
      position: 'relative',
      overflow: 'hidden',
      background: 'var(--earth-bg)',
      fontFamily: 'var(--font-sans)',
      isolation: 'isolate',
    }}>
      {/* Layer 0 — media. sunset-1.jpeg gives the sky a clean canvas. */}
      {useVideo ? (
        <video
          autoPlay muted loop playsInline
          poster={heroImageUrl}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: compact ? '50% 42%' : 'center 45%',
            zIndex: 0, display: 'block',
          }}
        >
          <source src={heroVideoUrl} type="video/mp4" />
          {heroVideoWebmUrl && <source src={heroVideoWebmUrl} type="video/webm" />}
        </video>
      ) : (
        <img
          src={heroImageUrl}
          alt="Mount Lavinia balcony at sunset — golden-hour sky over the Indian Ocean."
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: compact ? '50% 42%' : 'center 45%',
            zIndex: 0, display: 'block',
          }}
        />
      )}

      {/* Layer 1 — dual-band scrim for readability + dimmed sky so the neon reads. */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: compact
          ? 'linear-gradient(180deg, rgba(12,8,4,0.82) 0%, rgba(12,8,4,0.58) 26%, rgba(12,8,4,0.48) 54%, rgba(12,8,4,0.82) 80%, rgba(12,8,4,0.95) 100%)'
          : 'linear-gradient(180deg, rgba(12,8,4,0.78) 0%, rgba(12,8,4,0.54) 22%, rgba(12,8,4,0.46) 56%, rgba(12,8,4,0.80) 82%, rgba(12,8,4,0.95) 100%)',
      }} />

      {/* Layer 3 — foreground copy. Two zones: co-brand lockup top, voice line bottom. */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: `${padTop} ${padX} ${padBottom}`,
        color: '#FFFFFF',
      }}>

        {/* Top — centered co-brand lockup. Neon wordmark + optional Presented-by card. */}
        <div style={{
          width: '100%',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center',
        }}>
          <div
            className="hero-c-brand-lockup"
            data-compact={compact ? 'true' : undefined}
            data-has-sponsor={hasSponsor ? 'true' : undefined}
            style={heroLayoutVars(heroLayout) as CSSProperties}
          >
            <span className="hero-c-neon-mark" data-lockup-part="wordmark" data-lockup-midline="hyphen">
              <NeonWordmark
                id={compact ? 'neon-hero-m' : 'neon-hero-d'}
                height={wordmarkHeight}
                clipOverflow={hasSponsor}
              />
            </span>

            {hasSponsor && <span className="hero-c-brand-x" data-lockup-part="separator" aria-hidden>×</span>}

            {/* Sponsor co-brand card — only rendered if sponsorLogo is provided */}
            {hasSponsor && (
              <div className="hero-c-sponsor-card" data-lockup-part="sponsor">
                <span className="hero-c-presented-by">
                  Presented by
                </span>
                <img
                  className="hero-c-sponsor-logo"
                  data-lockup-part="sponsor-logo"
                  src={sponsorLogo}
                  alt={sponsorName ?? 'Sponsor'}
                  style={heroLayout?.logo_max_height ? { height: heroLayout.logo_max_height } : undefined}
                />
              </div>
            )}
          </div>

          {/* Season label — centered below the lockup */}
          <div className="hero-c-season-label" style={{
            marginTop: hasSponsor ? (compact ? 16 : 24) : (compact ? 22 : 32),
            fontSize: compact ? 'var(--text-sm)' : 'var(--text-base)',
            color: 'rgba(255,255,255,0.72)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 500,
          }}>
            {seasonLabel}
          </div>

          {showHeadline ? (
            <h1 className="hero-c-proposal-title">{headline}</h1>
          ) : (
            <h1 style={{
              position: 'absolute',
              width: 1, height: 1, overflow: 'hidden',
              clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
            }}>
              {headline || 'Yan-Vada (යන්-වාද)'}
            </h1>
          )}

        </div>

        {/* Bottom — Sinhala tagline + English voice line, left-edge aligned. */}
        <div className="hero-c-bottom-copy" style={{ maxWidth: compact ? '100%' : 'min(680px, 100%)' }}>
          {/* Sinhala tagline — three lines, heavier weight, slight text shadow */}
          <div
            className="si"
            aria-hidden
            style={{
              fontSize: compact ? 16 : 'clamp(16px, 2vw, 22px)',
              lineHeight: 1.55,
              color: '#FFFFFF',
              opacity: 0.95,
              textShadow: '0 1px 2px rgba(20,14,8,0.55), 0 2px 14px rgba(20,14,8,0.40)',
              marginBottom: compact ? 18 : 26,
              textAlign: 'left',
              width: '100%',
            }}
          >
            {SINHALA_POEM.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>

          {/* English voice line — founder-authored Tier C corpus */}
          <p style={{
            margin: 0,
            fontSize: compact ? 'var(--text-lg)' : 'clamp(1.125rem, 2.4vw, 26px)',
            lineHeight: 1.45,
            fontWeight: 400,
            color: '#FFFFFF',
            textShadow: '0 1px 2px rgba(20,14,8,0.55), 0 2px 20px rgba(20,14,8,0.45)',
            textWrap: 'pretty',
            letterSpacing: '-0.005em',
          }}>
            Bridge the gap between movers and their audience. We ask the hard questions. Sunsets are optional.
          </p>
          {showCtaActions && (
            <div className="hero-c-action-cluster">
              <div className="hero-c-tagline-actions" aria-label="Yan-Vada actions">
                <button className="btn btn-secondary" type="button" onClick={handleSneakPeekClick}>Watch Sneak-Peek</button>
                {partnershipDisabled ? (
                  <span className="btn btn-primary btn-disabled" aria-disabled="true">{partnershipDisabledLabel}</span>
                ) : (
                  <a className="btn btn-primary" href={partnershipHref}>{partnershipDisabledLabel}</a>
                )}
              </div>
              {!!activeSocialLinks.length && (
                <div className="hero-c-social-links" aria-label="Host social links">
                  {activeSocialLinks.map((link) => (
                    <a
                      key={`${link.platform}-${link.href}`}
                      className="hero-c-social-link"
                      data-platform={link.platform}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label ?? link.platform}
                      title={link.label ?? link.platform}
                    >
                      <PlatformIcon platform={link.platform} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showScrollCue && (
        <button className="hero-c-scroll-cue" type="button" onClick={handleScrollCueClick} aria-label="Scroll to proposal details">
          <span className="hero-c-scroll-cue-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </button>
      )}
      {sneakPeekOpen && (
        <div className="sneak-peek-modal" role="dialog" aria-modal="true" aria-labelledby="sneak-peek-title">
          <button className="sneak-peek-backdrop" type="button" aria-label="Close sneak-peek player" onClick={closeSneakPeek} />
          <section className="sneak-peek-panel">
            <div className="sneak-peek-header">
              <div>
                <span className="legal-eyebrow">Yan-Vada preview</span>
                <h2 id="sneak-peek-title">Season 01 Sneak-Peek</h2>
              </div>
              <button className="sneak-peek-close" type="button" onClick={closeSneakPeek} aria-label="Close sneak-peek player">×</button>
            </div>
            <div className="sneak-peek-frame">
              <video
                ref={sneakPeekVideoRef}
                src={SNEAK_PEEK_VIDEO_URL}
                poster={heroImageUrl}
                controls
                autoPlay
                playsInline
                preload="metadata"
              />
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
